import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IPublication } from '@opengov/stories-shared/types';

import { onlinePublication as messages } from '../../../../../locale/stories.en.locale.json';
import {
  generateOnlinePublicationUrl,
  GenerateOnlinePublicationUrlParams
} from '../../../../utils/generateOnlinePublicationUrl';
import { useToast } from '../../../Toast/context/useToast.context';
import { useUpdatePublicationUpdatedAt } from '../../PublicationView/hooks/usePublicationMutations';
import { publicationViewQueryKeys } from '../../PublicationView/hooks/publicationViewQueryKeys';

import { setupOnlinePublicationQueryKeys } from './setupOnlinePublicationQueryKeys';

export type UseGenerateOnlinePublicationUrlContext = {
  entitySubdomain: string;
};

export type GenerateOnlinePublicationUrlMutationVariables = Omit<
  GenerateOnlinePublicationUrlParams,
  'entitySubdomain'
>;

export function useGenerateOnlinePublicationUrl(context: UseGenerateOnlinePublicationUrlContext) {
  const { toggleToast } = useToast();
  const queryClient = useQueryClient();
  const updatePublicationUpdatedAt = useUpdatePublicationUpdatedAt();

  return useMutation<string, Error, GenerateOnlinePublicationUrlMutationVariables>({
    mutationKey: [...setupOnlinePublicationQueryKeys.all, 'generateOnlinePublicationUrl', context],
    mutationFn: (variables) =>
      generateOnlinePublicationUrl({
        ...variables,
        entitySubdomain: context.entitySubdomain
      }),
    onSuccess: async (_url, variables) => {
      if (variables.isPreview) return;

      // Update publication timestamp + invalidate publication query
      // (replaces compose-app effect calls removed from generateOnlinePublicationUrl util)
      await updatePublicationUpdatedAt.mutateAsync({
        publicationId: variables.publication.id,
        userId: variables.userId
      });

      await queryClient.invalidateQueries({
        queryKey: publicationViewQueryKeys.publication({ publicationId: variables.publication.id })
      });

      toggleToast({
        title: !variables.update ? messages.publicationLinkGenerated : messages.linkUpdated,
        body: !variables.update ? messages.changesWhereUpdated : messages.nowYouCanCopy,
        severity: 'success'
      });
    },
    onError: () => {
      toggleToast({
        title: messages.somethingWentWrongError,
        body: messages.failedToGenerateTheLink,
        severity: 'error'
      });
    }
  });
}

export type { IPublication };
